import { useCallback } from "react";
import { VoiceAI } from "../types/types";

/**
 * Custom hook that provides voice ID resolver functions.
 *
 * These resolvers handle the conversion between:
 * - VoiceAI ID (used in dropdowns/UI): voice.id or voice.value
 * - voiceCloneDbId (Prisma ID used by API): voice.voiceCloneDbId
 * - promptVoiceDbId (Prisma ID used by API): voice.promptVoiceDbId
 *
 * @param voiceOptions - Array of voice options from getVoices API
 * @returns Object containing resolver functions
 */
export const useVoiceResolvers = (voiceOptions: VoiceAI[]) => {
    /**
     * Resolves VoiceAI ID (dropdown value) to the appropriate ID for API calls.
     *
     * NOTE: This is used for audio generation APIs.
     * For translation APIs, use resolveVoiceIdForTranslation instead.
     *
     * This is needed because:
     * - For clone voices: The dropdown uses VoiceAI ID, but APIs expect voiceCloneDbId
     * - For prompt voices: The dropdown already uses externalVoiceId (ElevenLabs ID), so no conversion needed
     * - For regular voices: No conversion needed
     *
     * @param voiceId - VoiceAI ID from dropdown/UI
     * @returns voiceCloneDbId for cloned voices, original voiceId for prompt/regular voices
     */
    const resolveVoiceIdForApi = useCallback(
        (voiceId?: string | null): string | null => {
            if (!voiceId) {
                return voiceId || null;
            }
            // Find voice by id or value (TextToSpeech uses value, Translation uses id)
            const voice = voiceOptions.find(
                (voiceOption) => voiceOption.id === voiceId || voiceOption.value === voiceId,
            );
            // For clone voices: convert to voiceCloneDbId (backend needs this to look up externalVoiceId)
            if (voice && voice.voiceCloneDbId) {
                return voice.voiceCloneDbId;
            }
            // For prompt voices: DO NOT convert - the id is already the externalVoiceId (ElevenLabs ID)
            // The backend returns prompt voices with id: pv.externalVoiceId
            // So the dropdown value is already the correct ElevenLabs ID
            return voiceId;
        },
        [voiceOptions],
    );

    /**
     * Resolves VoiceAI ID for translation API calls.
     *
     * For translations, we send the VoiceAI ID directly (not voiceCloneDbId/promptVoiceDbId).
     * The backend will handle the conversion to externalVoiceId for ElevenLabs.
     *
     * @param voiceId - VoiceAI ID from dropdown/UI
     * @returns The VoiceAI ID as-is (no conversion needed for translations)
     */
    const resolveVoiceIdForTranslation = useCallback((voiceId?: string | null): string | null => {
        // For translations, send the VoiceAI ID directly - backend handles conversion
        return voiceId || null;
    }, []);

    /**
     * Reverse resolver: converts voiceCloneId (Prisma ID) to VoiceAI ID (dropdown value).
     *
     * This is needed because:
     * - The API returns voiceCloneId (Prisma ID)
     * - The dropdown uses VoiceAI ID (voice.id or voice.value)
     *
     * @param voiceCloneId - voiceCloneDbId from API response
     * @returns VoiceAI ID (voice.id or voice.value) for dropdown, or null if not found
     */
    const resolveVoiceIdFromClone = useCallback(
        (voiceCloneId?: string | null): string | null => {
            if (!voiceCloneId) {
                return null;
            }
            // Find the voice option where voiceCloneDbId matches the voiceCloneId from API
            const voice = voiceOptions.find((voiceOption) => voiceOption.voiceCloneDbId === voiceCloneId);
            if (voice) {
                // Return id if available, otherwise value (for compatibility with both formats)
                return voice.id || voice.value || null;
            }
            return null;
        },
        [voiceOptions],
    );

    /**
     * Reverse resolver: converts voicePromptId (Prisma ID) to VoiceAI ID (dropdown value).
     *
     * This is needed because:
     * - The API returns voicePromptId (Prisma ID)
     * - The dropdown uses VoiceAI ID (voice.id or voice.value)
     *
     * @param voicePromptId - promptVoiceDbId from API response
     * @returns VoiceAI ID (voice.id or voice.value) for dropdown, or null if not found
     */
    const resolveVoiceIdFromPrompt = useCallback(
        (voicePromptId?: string | null): string | null => {
            if (!voicePromptId) {
                return null;
            }
            // Find the voice option where promptVoiceDbId matches the voicePromptId from API
            const voice = voiceOptions.find((voiceOption) => voiceOption.promptVoiceDbId === voicePromptId);
            if (voice) {
                // Return id if available, otherwise value (for compatibility with both formats)
                return voice.id || voice.value || null;
            }
            return null;
        },
        [voiceOptions],
    );

    return {
        resolveVoiceIdForApi,
        resolveVoiceIdForTranslation,
        resolveVoiceIdFromClone,
        resolveVoiceIdFromPrompt,
    };
};
